import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatSelect } from '@angular/material/select';
import { take } from 'rxjs/operators';
import { ListItem } from '../index';
import { PFieldAbstractComponent } from '../p-field-abstract';
import { ButtonTypeEnum } from '@panaya/ui/p-button';
import { IPMenuButton } from '../../p-menu/menu-button.interface';
import { PMenuUtils } from '../../p-menu';
import { groupBy, toLowerCase } from '@frontend-site/shared/utils';
import { MatMenu } from '@angular/material/menu';

@Component({
    selector: 'p-picker-single-select',
    template: `
        <div class="p-menu-container" (click)="$event.stopPropagation()">
            <div *ngIf="canSearch" class="p-menu-header no-padding">
                <p-field-outline-text
                    class="p-menu-input"
                    [isSearch]="true"
                    [autoFocus]="true"
                    (search)="touch($event)"
                    (clearSearch)="touch($event)"
                    (keydown.arrowdown)="focusElemList()"
                    (keydown.enter)="toggleLoneSearchElem()"
                ></p-field-outline-text>
            </div>

            <div class="p-menu-body single-select">
                <mat-selection-list #singleSelectElem class="p-menu-body-container" #optionsList="matSelectionList" [multiple]="false">
                    <cdk-virtual-scroll-viewport
                        itemSize="32"
                        minBufferPx="320"
                        maxBufferPx="320"
                        [style.height.px]="getHeight()"
                        class="select-viewport"
                    >
                        <mat-list-option
                            class="p-menu-single-item"
                            *ngIf="this.isEmptyOptionToShow()"
                            [value]="emptyOption[idField]"
                            [attr.automation-id]="'p-picker-single-select-option_' + emptyOption[nameField]"
                            (click)="toggleValue(emptyOption[idField])"
                            (keydown.enter)="toggleValue(emptyOption[idField])"
                        >
                            {{ emptyOption[nameField] }}
                        </mat-list-option>
                        <ng-container *cdkVirtualFor="let item of list | filter: searchPredicate:searchCriteria; index as i">
                            <span
                                *ngIf="shouldDisplayTitle(list | filter: searchPredicate:searchCriteria, i)"
                                class="p-menu-group-title p-headline-6"
                            >
                                {{ item[groupField] }}
                            </span>
                            <div [matTooltip]="item.disable ? itemTooltip(item) : ''">
                                <div *ngIf="item.nestedItems; else standardOption">
                                    <p-action-button
                                        label="{{ item.name }}"
                                        [isMenuTrigger]="true"
                                        [isRight]="true"
                                        [isStandalone]="false"
                                        [disabled]="disableItem(item)"
                                        #menuTrigger="matMenuTrigger"
                                        [matMenuTriggerFor]="exportMenu"
                                    ></p-action-button>
                                    <mat-menu #exportMenu="matMenu" class="no-padding p-menu p-menu-single sub-menu">
                                        <ng-template matMenuContent>
                                            <p-picker-single-select
                                                [canSearch]="false"
                                                [value]=""
                                                [list]="item.nestedItems"
                                                (valueChange)="optionSelected($event, item.nestedItems); exportMenu.closed.emit()"
                                            >
                                            </p-picker-single-select>
                                        </ng-template>
                                    </mat-menu>
                                </div>
                                <ng-template #standardOption>
                                    <mat-list-option
                                        class="p-menu-single-item"
                                        [ngClass]="{
                                            'p-menu-single-item--divider': item.hasTrailingDivider
                                        }"
                                        [value]="item[idField]"
                                        [attr.automation-id]="'p-picker-single-select-option_' + item[nameField]"
                                        [selected]="item | extract: selectedPredicate:value"
                                        [disabled]="disableItem(item)"
                                        (click)="toggleValue(item[idField])"
                                        (keydown.space)="toggleValue(item[idField])"
                                        (keydown.enter)="toggleValue(item[idField])"
                                        (mouseenter)="hoveredIconId = item.id"
                                        (mouseleave)="hoveredIconId = null"
                                    >
                                        <div
                                            class="p-mat-list-text-wrapper"
                                            [ngClass]="{
                                                'left-icon': !!hasIcon('left', item)
                                            }"
                                        >
                                            <p-icon
                                                class="p-menu-item-icon"
                                                *ngIf="hasIcon('left', item)"
                                                [name]="getIconName('left', item)"
                                                [color]="item.leftIcon?.color"
                                                [matTooltip]="item.tooltipText"
                                                matTooltipPosition="above"
                                                (click)="onIconClick($event, item, 'left')"
                                            ></p-icon>
                                            <span [matTooltip]="item[nameField]" [showOnlyWhenEllipsis]="true" class="p-menu-item-text">{{
                                                item[nameField]
                                            }}</span>
                                        </div>
                                        <p-icon
                                            class="p-menu-item-icon"
                                            *ngIf="hasIcon('right', item)"
                                            [name]="getIconName('right', item)"
                                            [color]="item.rightIcon?.color"
                                            [matTooltip]="item.rightIconTooltipText || item.tooltipText"
                                            matTooltipPosition="above"
                                            (click)="onIconClick($event, item, 'right')"
                                        ></p-icon>
                                    </mat-list-option>
                                </ng-template>
                            </div>
                        </ng-container>
                        <div class="p-menu-empty" *ngIf="!(list | filter: searchPredicate:searchCriteria)?.length">
                            <p class="p-menu-empty-state p-paragraph-1">
                                {{ noData }}
                            </p>
                        </div>
                    </cdk-virtual-scroll-viewport>
                </mat-selection-list>
            </div>
            <div class="p-menu-footer" *ngIf="isFooterButtonsDisplayed(footerButtons)">
                <div *ngFor="let btn of footerButtons">
                    <p-button
                        *ngIf="!btn.displayed || btn.displayed(selectedItem)"
                        class="p-menu-btn-line"
                        [label]="btn.label"
                        [iconBefore]="btn.icon?.name"
                        [iconBeforeSize]="btn.icon?.size ?? 'md'"
                        [iconBeforeColor]="btn.icon?.color ?? 'base-dark'"
                        [buttonType]="footerButtonType"
                        (buttonClicked)="executeBtnClick(selectedItem, btn)"
                    ></p-button>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./p-picker-single-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PPickerSingleSelectComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'pPickerSingleSelect'
})
export class PPickerSingleSelectComponent extends PFieldAbstractComponent<number | string | null> implements OnInit, AfterViewInit {
    pMenuUtils = new PMenuUtils();
    searchCriteria: string = '';
    singleSearchElem: any;
    display = true;
    footerButtonType = ButtonTypeEnum.Tertiary;
    selectedItem: any;
    numOfDisplayedItems: number = 0;
    numOfDisplayedButtons: number = 0;
    numOfGroups: number = 0;
    public hoveredIconId = null;

    @HostBinding('class.ag-custom-component-popup') popup = true;
    @Input() list: ListItem[] = [];
    @Input() parent?: MatMenu;
    @Input() customClasses?: string;
    @Input() placeholder = '';
    @Input() idField: string = 'id';
    @Input() nameField: string = 'name';
    @Input() groupField: string = '';
    @Input() emptyOption?: any;
    @Input() useFullScreenHeight = false;
    @Input() noData: string = 'No Data Found';
    @Input() autoFocus = false;
    @Input() canSearch = true;
    @Input() footerButtons: IPMenuButton[] = [];
    /**
     * @deprecated Please use {@link rightIcon} or {@link leftIcon} instead.
     */
    @Input() iconsPosition: string = 'right';
    @Input() height?: number;
    @Output() searchChanged = new EventEmitter<string>();
    /**
     * @deprecated Please use {@link leftIconClicked} or  {@link rightIconClicked} instead.
     */
    @Output() iconClicked = new EventEmitter<string>();
    @Output() leftIconClicked = new EventEmitter<ListItem>();
    @Output() rightIconClicked = new EventEmitter<ListItem>();

    @ViewChild(CdkVirtualScrollViewport, { static: false }) viewPort: CdkVirtualScrollViewport = {} as CdkVirtualScrollViewport;
    @ViewChild('singleSelectElem', { static: false }) singleSelectElem: MatSelect = {} as MatSelect;

    public readonly AUTOMATION_ID_PREFIX = 'p-picker-single-select-option_';

    constructor(cd: ChangeDetectorRef) {
        super(cd);
    }

    ngOnInit(): void {
        this.filterOutHiddenItems();
        this.numOfDisplayedItems = this.list.length;
        this.selectedItem = this.list.find((l: any) => l[this.idField] == this.value);
        this.numOfDisplayedButtons = this.footerButtons?.filter(
            (button) => !button.displayed || button.displayed(this.selectedItem)
        ).length;
        if (this.groupField) {
            this.numOfGroups = Object.keys(groupBy(this.list, this.groupField)).length;
        }
        const selectedIndex = this.list.indexOf(this.selectedItem);
        if (selectedIndex > -1) {
            setTimeout(() => {
                let indexToScroll = this.emptyOption ? selectedIndex + 1 : selectedIndex;
                this.viewPort.scrollToIndex(indexToScroll);
                let range = this.viewPort.getRenderedRange();
                if (selectedIndex > range.end && this.viewPort.getViewportSize() && selectedIndex >= this.list.length - 4) {
                    this.viewPort.scrolledIndexChange.pipe(take(1)).subscribe(() => this.viewPort.scrollToIndex(indexToScroll));
                }
            }, 50);
        }
    }

    private filterOutHiddenItems() {
        this.list = this.list.filter((item) => !item.hidden);
    }

    ngAfterViewInit() {
        if (!this.canSearch) {
            this.singleSelectElem.focus();
        }
    }

    selectedPredicate = (value: any) => (item: any) => value == item[this.idField];

    searchPredicate: (substring: string) => (ListItem: any) => boolean = (substring) => (item) => {
        return (
            item[this.idField] === this.emptyOption?.[this.idField] ||
            toLowerCase(item[this.nameField]).includes(toLowerCase(substring).trim())
        );
    };

    touch(value: any) {
        this.searchCriteria = value;
        const filteredlist = this.list.filter((entity: any) => toLowerCase(entity[this.nameField]).includes(toLowerCase(value).trim()));
        this.numOfDisplayedItems = filteredlist.length;
        this.singleSearchElem = this.numOfDisplayedItems === 1 ? filteredlist[0] : null;
        if (this.groupField) {
            this.numOfGroups = Object.keys(groupBy(filteredlist, this.groupField)).length;
        }
        this.searchChanged.emit(this.searchCriteria);
        this.markAsTouched();
    }

    toggleValue(val: any) {
        this.markAsTouched();
        if (this.value !== val) {
            this.value = val;
        }

        this._onChange(this.value);
        this.cd.detectChanges();
    }

    focusOptions(optionsList: MatSelect) {
        optionsList?.focus();
    }

    shouldDisplayTitle(filtered: any, index: number) {
        if (this.groupField && filtered && filtered.length > 0) {
            if (index === 0) {
                return true;
            }
            if (filtered[index][this.groupField] !== filtered[index - 1][this.groupField]) {
                return true;
            }
        }
        return false;
    }

    public onIconClick(event: Event, item: ListItem, position: IconPosition): void {
        if (position === 'left' && item.leftIcon?.showOnlyOnHover) {
            event.stopPropagation();
            this.leftIconClicked.emit(item);
        } else if (position === 'right' && item.rightIcon?.showOnlyOnHover) {
            event.stopPropagation();
            this.rightIconClicked.emit(item);
        }
        if (item.showIconOnHover && position === 'right') {
            event.stopPropagation();
            this.iconClicked.emit(item.iconLink);
        }
    }

    executeBtnClick(value: number | undefined, btn: IPMenuButton) {
        btn.click(value);
    }

    disableItem(item: ListItem): boolean {
        if (typeof item.disable === 'function') {
            return item.disable();
        }
        return <boolean>item.disable;
    }

    itemTooltip(item: any) {
        if (typeof item.tooltip === 'function') {
            return item.tooltip();
        }
        return '';
    }

    isEmptyOptionToShow() {
        return this.emptyOption;
    }

    getHeight() {
        return (
            this.height ||
            this.pMenuUtils.calcHeight(
                this.numOfGroups,
                this.numOfDisplayedItems,
                this.emptyOption,
                this.useFullScreenHeight,
                this.numOfDisplayedButtons
            )
        );
    }

    focusElemList() {
        this.focusOptions(this.singleSelectElem);
    }

    toggleLoneSearchElem() {
        if (this.numOfDisplayedItems === 1) {
            if (this.singleSearchElem) {
                this.toggleValue(this.singleSearchElem[this.idField]);
            }
        }
    }

    isFooterButtonsDisplayed(footerButtons: IPMenuButton[]) {
        return footerButtons?.filter((btn) => btn.displayed(this)).length > 0;
    }

    optionSelected(itemId: any, items: any[]) {
        items.find((item) => item.id === itemId).onClick();
        if (!!this.parent) {
            this.parent.closed.emit();
        }
    }

    protected hasIcon(position: IconPosition, item: ListItem): boolean {
        if (position === 'right') {
            const isDefaultRightIcon = item.iconName && this.iconsPosition === 'right';
            const onlyOnHover = isDefaultRightIcon ? item.showIconOnHover : item.rightIcon?.showOnlyOnHover;
            const iconName = isDefaultRightIcon ? item.iconName : item.rightIcon?.name;
            const isItemHover = this.hoveredIconId === item.id;
            return ((onlyOnHover && isItemHover) || !onlyOnHover) && !!iconName;
        }
        if (position === 'left') {
            const isDefaultLeftIcon = item.iconName && this.iconsPosition === 'right';
            const onlyOnHover = isDefaultLeftIcon ? item.showIconOnHover : item.leftIcon?.showOnlyOnHover;
            const iconName = isDefaultLeftIcon ? item.iconName : item.leftIcon?.name;
            const isItemHover = this.hoveredIconId === item.id;
            return ((onlyOnHover && isItemHover) || !onlyOnHover) && !!iconName;
        }
        return false;
    }

    protected getIconName(position: IconPosition, item: ListItem): string {
        if (position === 'right') {
            const isDefaultRightIcon = !!item.iconName && this.iconsPosition === 'right';
            return isDefaultRightIcon ? (item.iconName as string) : item.rightIcon?.name || '';
        }
        const isDefaultLeftIcon = !!item.iconName && this.iconsPosition === 'left';
        return isDefaultLeftIcon ? (item.iconName as string) : item.leftIcon?.name || '';
    }
}

type IconPosition = 'right' | 'left';
